import { Formik, FormikHelpers } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form, Row } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import styled from "styled-components";
import { object, Schema, string } from "yup";
import {
  apiConfiguration,
  ReCAPTCHASiteKey,
  withCsrf,
} from "../../api-configuration";
import { ErrorsListCard } from "../../components/forms/errors-list";
import { TextFieldFormGroup } from "../../components/forms/groups/text-field-group";
import { FormState } from "../../components/forms/form-state";
import { logError } from "../../errors";
import { trackGaEvent } from "../../util/gtm";
import { CreatePromotionLeadForm, LeadApi } from "../../lib-api";
import { SubmitState } from "../../util/loading";

type Form = Omit<CreatePromotionLeadForm, "promotionId">;

const validationSchema: Schema<Form> = object().shape({
  name: string().label("Navn").required(),
  phone: string().label("Telefonnummer").required(),
  email: string().label("Email").email().required(),
  recaptcha: string().label("Bevis du ikke er en robot").required(),
});

function getInitialValues(): Form {
  return { name: "", phone: "", email: "", recaptcha: "" };
}

const api = new LeadApi(apiConfiguration);

// How long the "Tak!" confirmation stays up before we send the visitor back to
// the offers list.
const REDIRECT_DELAY_MS = 5000;

// Renders inside the white "offer" rail on the VIP page, so the form blends in
// rather than drawing its own coloured box. The form mounts late (separate
// bundle), so it fades up once mounted instead of snapping into the reserved
// space below the price.
const Container = styled("div")({
  color: "inherit",
  opacity: 0,
  transform: "translateY(6px)",
  transition: "opacity 150ms ease-out, transform 150ms ease-out",
  '&[data-mounted="true"]': {
    opacity: 1,
    transform: "none",
  },
});

export const PromotionCtaForm: React.FC<{
  promotionId: string;
  csrf: string;
  srpUrl?: string;
  customerId?: string;
  businessId?: string;
}> = ({ promotionId, csrf, srpUrl, customerId, businessId }) => {
  const recaptchaRef = useRef<ReCAPTCHA>();
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    state: SubmitState.None,
  });
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Once submitted, redirect back to the offers list after a short pause so the
  // visitor still sees the confirmation. Skipped when no SRP URL was provided.
  useEffect(() => {
    if (formState.state !== SubmitState.Submitted || !srpUrl) return;
    const timer = window.setTimeout(
      () => window.location.assign(srpUrl),
      REDIRECT_DELAY_MS
    );
    return () => window.clearTimeout(timer);
  }, [formState.state, srpUrl]);

  const handleSubmit = useCallback(
    async (values: Form, helpers: FormikHelpers<Form>) => {
      try {
        setFormState({ state: SubmitState.None });

        await withCsrf(api, csrf).createPromotionLead({
          createPromotionLeadForm: {
            promotionId,
            ...values,
          },
        });

        helpers.resetForm();
        setFormState({ state: SubmitState.Submitted });
        trackGaEvent("lead_generated", {
          item_id: promotionId,
          customer_id: customerId,
          business_id: businessId,
          container: "promotion_card",
        });
      } catch (e) {
        if (e instanceof Response && e.status === 400) {
          const json = await e.json();
          setFormState({ state: SubmitState.Error, errors: json.errors });
          e = new Error(JSON.stringify(json));
        } else {
          setFormState({ state: SubmitState.Error });
        }
        if (!(e instanceof Error)) e = new Error(JSON.stringify(e));
        logError(e);
        recaptchaRef.current?.reset();
        helpers.setFieldValue("recaptcha", "");
      }
    },
    [promotionId, csrf, customerId, businessId]
  );

  if (formState.state === SubmitState.Submitted) {
    return (
      <Container data-mounted={mounted}>
        <h2>Tak! Forretningen kontakter dig.</h2>
        {srpUrl && (
          <p className="text-muted">
            Du sendes tilbage til alle tilbud om et øjeblik…
          </p>
        )}
      </Container>
    );
  }

  return (
    <Container data-mounted={mounted}>
      <h2>Hent tilbud</h2>
      <Formik<Form>
        initialValues={getInitialValues()}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({
          handleChange,
          values,
          touched,
          errors,
          isSubmitting,
          setFieldValue,
          handleSubmit,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <TextFieldFormGroup
                fieldName="name"
                className="col-12 col-md-6"
                fieldDisplayName="Navn *"
                placeholder="Dit navn"
                error={touched.name ? errors.name : undefined}
                value={values.name}
                disabled={isSubmitting}
                handleChange={handleChange}
              />
              <TextFieldFormGroup
                fieldName="phone"
                className="col-12 col-md-6"
                fieldDisplayName="Telefonnummer *"
                placeholder="Dit telefonnummer"
                error={touched.phone ? errors.phone : undefined}
                value={values.phone}
                disabled={isSubmitting}
                handleChange={handleChange}
              />
              <TextFieldFormGroup
                fieldName="email"
                className="col-12"
                fieldDisplayName="Email *"
                placeholder="Din email"
                error={touched.email ? errors.email : undefined}
                value={values.email}
                disabled={isSubmitting}
                handleChange={handleChange}
              />
              {/* Reserve the reCAPTCHA widget's fixed footprint so the late-
                  loading iframe doesn't shove the submit button when it appears.
                  The widget is 304x78; width is 334 because this is a Bootstrap
                  column (border-box) with 15px gutter padding each side, so the
                  304px widget needs 304 + 2*15 of column width to fit without
                  growing. display:flex blockifies the inline-block widget,
                  removing the baseline descender gap that would otherwise make
                  it render a few px taller than the reserved height. */}
              <Form.Group
                className="col-12 col-md-auto"
                style={{ display: "flex", minWidth: 334, minHeight: 78 }}
              >
                <ReCAPTCHA
                  sitekey={ReCAPTCHASiteKey}
                  hl="da"
                  onChange={(token) =>
                    setFieldValue("recaptcha", token ?? "")
                  }
                  ref={recaptchaRef}
                />
              </Form.Group>
              <Form.Group className="col-12 col-md-auto d-flex align-items-center">
                <Button
                  disabled={isSubmitting}
                  variant="primary"
                  type="submit"
                  size="lg"
                  onClick={() => setSubmitAttempted(true)}
                >
                  Hent tilbud
                </Button>
              </Form.Group>
            </Row>
          </Form>
        )}
      </Formik>
      {submitAttempted && formState.state === SubmitState.Error && (
        <div className="mt-2">
          {formState.errors ? (
            <ErrorsListCard
              title="Vores server meldte følgende fejl"
              errors={formState.errors}
            />
          ) : (
            <>Ups, der opstod en fejl. Prøv evt. at genindlæse siden.</>
          )}
        </div>
      )}
    </Container>
  );
};
